import React, { useMemo } from "react";
import { GlobalModal } from "../../../../global/GlobalModal";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryGridSection,
} from "../../../../global/directory";
import { useRoleSectionContext } from "../RoleSectionContext";

export default function RoleDirectorySection() {
  const {
    directoryError,
    listLoading,
    page,
    totalPages,
    total,
    setPage,
    rows,
    busy,
    startEdit,
    rolePendingRemoval,
    removingRole,
    requestRoleRemoval,
    cancelRoleRemoval,
    confirmRoleRemoval,
    pageSize,
    isSystemRoleRow,
  } = useRoleSectionContext();

  const tableHeaders = useMemo(
    () => [
      { key: "role_name", label: "Role" },
      { key: "role_slug", label: "Slug" },
      { key: "description", label: "Description" },
      { key: "actions", label: "Actions", className: "ub-table__col-actions" },
    ],
    [],
  );

  const list = rows || [];
  const showRows = !listLoading && list.length > 0;

  return (
    <div id="ub-roles-directory">
      <GlobalDirectoryShell
        filters={
          directoryError ? (
            <p className="ub-form-alert ub-form-alert--error" role="alert">
              {directoryError}
            </p>
          ) : null
        }
        grid={
          <GlobalDirectoryGridSection
            loading={listLoading}
            loadingMessage="Loading roles…"
            headers={tableHeaders}
            isEmpty={!listLoading && list.length === 0}
            emptyMessage="No active roles found."
            tableAriaLabel="Roles directory table"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {showRows
              ? list.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {row.role_name}
                      {isSystemRoleRow(row) ? (
                        <span className="ub-badge ub-badge--system">System</span>
                      ) : null}
                    </td>
                    <td>{row.role_slug || "—"}</td>
                    <td>
                      {row.description && String(row.description).trim()
                        ? String(row.description)
                        : "—"}
                    </td>
                    <td className="ub-table__col-actions">
                      {isSystemRoleRow(row) ? (
                        <span className="ub-muted">Protected</span>
                      ) : (
                        <div className="ub-table-actions">
                          <GlobalTableActionButton
                            action="edit"
                            disabled={busy}
                            onClick={() => startEdit(row)}
                          />
                          <GlobalTableActionButton
                            action="remove"
                            disabled={busy}
                            onClick={() => requestRoleRemoval(row)}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              : null}
          </GlobalDirectoryGridSection>
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            label="Roles pagination"
          />
        }
      />

      <GlobalModal
        open={Boolean(rolePendingRemoval)}
        onClose={() => !removingRole && cancelRoleRemoval()}
        title="Remove role from directory?"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={cancelRoleRemoval}
              disabled={removingRole}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="danger"
              onClick={confirmRoleRemoval}
              loading={removingRole}
            >
              Remove role
            </GlobalButton>
          </div>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          Remove <strong>{rolePendingRemoval?.role_name}</strong> from the directory?
          The role will be marked inactive (soft delete) and will no longer appear in
          this list.
        </p>
      </GlobalModal>
    </div>
  );
}
